import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowDownUp, FileSpreadsheet } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtEuro, isoDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const pts = (n) => Math.round(n ?? 0).toLocaleString("it-IT", { useGrouping: "always" });
const pct = (n) => (n == null ? "—" : `${n.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`);
const COLS = [
  ["rank", "Pos.", (r) => r.rank], ["name", "Tecnico", (r) => r.name], ["company_code", "Società", (r) => r.company_code],
  ["counted", "Punti", (r) => pts(r.counted)], ["saturday", "Sabato", (r) => pts(r.saturday)], ["pending", "Da validare", (r) => pts(r.pending)],
  ["target", "Obiettivo", (r) => pts(r.target)], ["target_pct", "% obiettivo", (r) => pct(r.target_pct)], ["kpi", "KPI", (r) => pct(r.kpi)],
  ["incentive", "Punti incentivo", (r) => pts(r.incentive)], ["incentive_eur", "Incentivo €", (r) => fmtEuro(r.incentive_eur)],
];
const NUM = new Set(["counted", "saturday", "pending", "target", "target_pct", "kpi", "incentive", "incentive_eur", "rank"]);

const totalsBy = (rows) => {
  const g = {};
  rows.forEach((r) => {
    const t = (g[r.company_code] ||= { company_code: r.company_code, n: 0, counted: 0, saturday: 0, pending: 0, target: 0, incentive: 0, incentive_eur: 0, assigned: 0, completed_positive: 0 });
    t.n += 1; ["counted", "saturday", "pending", "target", "incentive", "incentive_eur", "assigned", "completed_positive"].forEach((k) => { t[k] += r[k] || 0; });
  });
  return Object.values(g).sort((a, b) => b.counted - a.counted);
};

export const TechLeaderboard = () => {
  const { companyId } = useData();
  const [month, setMonth] = useState(isoDate(new Date()).slice(0, 7));
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "rank", dir: 1 });
  useEffect(() => {
    setData(null);
    api.get("/production/leaderboard", { params: { month, ...(companyId ? { company_id: companyId } : {}) } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month, companyId]);
  const cols = COLS.filter(([k]) => k !== "incentive_eur" || data?.show_eur);
  const rows = useMemo(() => [...(data?.rows || [])].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    if (x == null || y == null) return (x == null) - (y == null);
    return (NUM.has(sort.key) ? x - y : String(x).localeCompare(String(y))) * sort.dir;
  }), [data, sort]);
  const download = async () => {
    try {
      const r = await api.get("/production/leaderboard", { params: { month, format: "xlsx", ...(companyId ? { company_id: companyId } : {}) }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `classifica_tecnici_${month}.xlsx`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="tech-leaderboard">
      <div className="flex flex-wrap items-end gap-3">
        <div><div className="micro-label mb-1">Mese</div><Input type="month" value={month} max={isoDate(new Date()).slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="leaderboard-month" /></div>
        <Button variant="outline" onClick={download} disabled={!rows.length} data-testid="leaderboard-download-xlsx"><FileSpreadsheet className="mr-2 h-4 w-4" />Scarica Excel</Button>
        <p className="text-xs text-muted-foreground">Punti = € di listino × coefficiente della società · sabati esclusi dall'incentivo · lavori di squadra divisi tra i membri</p>
      </div>
      <div className="overflow-x-auto border bg-white">
        <Table>
          <TableHeader><TableRow>{cols.map(([k, label]) => (
            <TableHead key={k} className={NUM.has(k) && k !== "rank" ? "text-right" : ""}>
              <button className="inline-flex items-center gap-1" onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : NUM.has(k) && k !== "rank" ? -1 : 1 }))} data-testid={`leaderboard-sort-${k}`}>{label}<ArrowDownUp className="h-3 w-3 opacity-50" /></button>
            </TableHead>))}</TableRow></TableHeader>
          <TableBody>
            {!data && <TableRow><TableCell colSpan={cols.length} className="text-muted-foreground">Calcolo in corso…</TableCell></TableRow>}
            {data && !rows.length && <TableRow><TableCell colSpan={cols.length} className="text-muted-foreground" data-testid="leaderboard-empty">Nessun tecnico per il mese scelto.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.employee_id} data-testid={`leaderboard-row-${r.employee_id}`}>
                {cols.map(([k, , f]) => <TableCell key={k} className={`${NUM.has(k) && k !== "rank" ? "text-right font-mono" : ""} ${k === "target_pct" && r.target_pct >= 100 ? "font-semibold text-emerald-700" : ""}`}>{f(r)}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
          {rows.length > 0 && <TableFooter data-testid="leaderboard-totals">
            {totalsBy(rows).map((t) => (
              <TableRow key={t.company_code} data-testid={`leaderboard-total-${t.company_code}`}>
                <TableCell colSpan={2} className="font-semibold">Totale {t.company_code} · {t.n} tecnici</TableCell><TableCell />
                <TableCell className="text-right font-mono">{pts(t.counted)}</TableCell><TableCell className="text-right font-mono">{pts(t.saturday)}</TableCell>
                <TableCell className="text-right font-mono">{pts(t.pending)}</TableCell><TableCell className="text-right font-mono">{pts(t.target)}</TableCell>
                <TableCell className="text-right font-mono">{t.target ? pct(Math.round((t.counted / t.target) * 1000) / 10) : "—"}</TableCell>
                <TableCell className="text-right font-mono">{t.assigned ? pct(Math.round((t.completed_positive / t.assigned) * 1000) / 10) : "—"}</TableCell>
                <TableCell className="text-right font-mono">{pts(t.incentive)}</TableCell>
                {data?.show_eur && <TableCell className="text-right font-mono">{fmtEuro(t.incentive_eur)}</TableCell>}
              </TableRow>
            ))}
          </TableFooter>}
        </Table>
      </div>
    </div>
  );
};
